/**
 * `hashtagAutocompleteExtension` — `#tag` decoration + live completion (editor-chat.md §2.1,
 * §2.2, §5 step 8).
 *
 * editor-chat.md §2.2: hashtag completion is triggered on `#`, filters the cached
 * `AutocompleteSources.tags` by prefix against `canonical` and `display`, ranks by `noteCount`
 * desc then alpha, and inserts the **`display`** form (PRD §8.2 — display preserves the casing
 * typed; the backend canonicalizes at ingest). Nested-tag segments (`.`) continue the
 * completion within the path.
 *
 * Two parts:
 *  - a **decoration view plugin** that recognizes `#tag` tokens in the *visible viewport only*
 *    and marks them with a clickable class — recomputed on viewport/doc change, NOT a
 *    full-document scan per keystroke, so it stays within the <100ms note-save perceived
 *    budget (PRD §11) at large note sizes (editor-chat.md §2.1);
 *  - a **completion source** that filters the cached `AutocompleteSourceStore.current().tags`
 *    *locally* — the fetch is the only network cost; keystroke-time filtering never awaits
 *    (editor-chat.md §2.2).
 *
 * The store is captured at extension-construction time (it has stable identity for the
 * editor's lifetime — see `useAutocompleteSources`).
 */

import type { CompletionContext, CompletionResult, CompletionSource } from '@codemirror/autocomplete';
import type { EditorView } from '@codemirror/view';
import { Decoration, type DecorationSet, ViewPlugin, type ViewUpdate } from '@codemirror/view';
import { RangeSetBuilder, type Extension } from '@codemirror/state';
import type { AutocompleteSourceStore } from '../autocomplete/sources';
import type { TagCompletionEntry } from '../editorTypes';

/**
 * Recognizes a `#tag` token: a `#` followed by one or more tag-path characters. Nested tags
 * use `.` segments (`#project.aletheia.v2`); hyphens and underscores are allowed in a segment.
 * The leading `(^|\s)` ensures `#` only starts a tag at a word boundary — not mid-word, and
 * not inside `##` markdown headings.
 */
const HASHTAG_TOKEN = /(^|\s)(#[A-Za-z0-9][A-Za-z0-9._-]*)/g;

/**
 * Matches the *in-progress* hashtag immediately left of the cursor — the completion trigger.
 * The captured group is the partial tag path *without* the `#`, which is what we prefix-match
 * against the cached tag set.
 */
const HASHTAG_BEFORE_CURSOR = /(?:^|\s)#([A-Za-z0-9._-]*)$/;

/** How many completions to surface — a long list is noise; the top matches by rank are enough. */
const MAX_HASHTAG_COMPLETIONS = 20;

/** The CSS class applied to a decorated `#tag` token — themed by the surface's stylesheet. */
const HASHTAG_DECORATION_CLASS = 'cm-aletheia-hashtag';

// ---------------------------------------------------------------------------
// Decoration view plugin — recompute on viewport change, NOT per keystroke scan
// ---------------------------------------------------------------------------

/**
 * Build the decoration set for the *visible viewport only* — every `#tag` token in the visible
 * line ranges gets a `mark` decoration. Scanning only the viewport (not the whole document) is
 * the editor-chat.md §2.1 performance contract: a 100K-character note does not get fully
 * re-scanned on every keystroke.
 */
function buildHashtagDecorations(view: EditorView): DecorationSet {
  const builder = new RangeSetBuilder<Decoration>();
  const mark = Decoration.mark({ class: HASHTAG_DECORATION_CLASS });
  for (const { from, to } of view.visibleRanges) {
    const text = view.state.doc.sliceString(from, to);
    HASHTAG_TOKEN.lastIndex = 0;
    let match: RegExpExecArray | null;
    while ((match = HASHTAG_TOKEN.exec(text)) !== null) {
      // match[1] is the leading boundary char (or ''), match[2] is the `#tag` token itself.
      const tokenStart = from + match.index + match[1].length;
      const tokenEnd = tokenStart + match[2].length;
      builder.add(tokenStart, tokenEnd, mark);
    }
  }
  return builder.finish();
}

/**
 * The `#tag` decoration view plugin. Rebuilds its decoration set on construction and whenever
 * the document or the viewport changes — never on a pure selection move, and never a
 * full-document scan (editor-chat.md §2.1).
 */
const hashtagDecorationPlugin = ViewPlugin.fromClass(
  class {
    decorations: DecorationSet;

    constructor(view: EditorView) {
      this.decorations = buildHashtagDecorations(view);
    }

    update(update: ViewUpdate): void {
      if (update.docChanged || update.viewportChanged) {
        this.decorations = buildHashtagDecorations(update.view);
      }
    }
  },
  { decorations: (plugin) => plugin.decorations },
);

// ---------------------------------------------------------------------------
// Completion source — filters the cached store locally
// ---------------------------------------------------------------------------

/**
 * Rank two tag entries for completion ordering: `noteCount` desc (frequent tags first), then
 * alphabetically by `canonical` (editor-chat.md §2.2). A stable, total comparator.
 */
function rankTags(a: TagCompletionEntry, b: TagCompletionEntry): number {
  if (a.noteCount !== b.noteCount) return b.noteCount - a.noteCount;
  return a.canonical.localeCompare(b.canonical);
}

/**
 * Build the hashtag completion source over a captured `AutocompleteSourceStore`.
 *
 * Triggered when the text immediately left of the cursor matches `HASHTAG_BEFORE_CURSOR`. It
 * filters `store.current().tags` *synchronously* by prefix against both `canonical` and
 * `display`, ranks the matches, and returns up to `MAX_HASHTAG_COMPLETIONS`. The applied label
 * is the **`display`** form (PRD §8.2). Returns `null` when the cursor is not in a hashtag
 * context — CM6 then offers no hashtag completions there.
 */
function makeHashtagCompletionSource(store: AutocompleteSourceStore) {
  return (context: CompletionContext): CompletionResult | null => {
    const before = context.matchBefore(HASHTAG_BEFORE_CURSOR);
    if (before === null) return null;
    // `before.text` includes the leading boundary + `#`; recover just the partial path.
    const partialMatch = HASHTAG_BEFORE_CURSOR.exec(before.text);
    const partial = (partialMatch?.[1] ?? '').toLowerCase();
    // An explicit `#` with nothing typed yet still opens the list (context.explicit handles a
    // manual trigger); only bail if there is genuinely no hashtag context.
    if (partial === '' && !context.explicit && !before.text.trim().endsWith('#')) {
      return null;
    }

    const tags = store.current().tags;
    const matches = tags
      .filter(
        (tag) =>
          tag.canonical.toLowerCase().startsWith(partial) ||
          tag.display.toLowerCase().startsWith(partial),
      )
      .sort(rankTags)
      .slice(0, MAX_HASHTAG_COMPLETIONS);

    // `from` is the start of the partial path (just after the `#`), so applying a completion
    // replaces the partial with the chosen `display` form.
    const from = context.pos - partial.length;
    return {
      from,
      options: matches.map((tag) => ({
        label: tag.display,
        // A nested tag continues the completion within the path — keep the popup open after
        // applying a path prefix so the user can drill into `project.aletheia.<...>`.
        type: tag.isNested ? 'namespace' : 'keyword',
        detail: tag.noteCount > 0 ? `${tag.noteCount} notes` : undefined,
        // Insert the display form verbatim — the backend canonicalizes at ingest (PRD §8.2).
        apply: tag.display,
      })),
      // The completion list is purely local — re-validate it as the user types more, against
      // the same cached set (no round-trip).
      validFor: /^[A-Za-z0-9._-]*$/,
    };
  };
}

// ---------------------------------------------------------------------------
// The composed extension
// ---------------------------------------------------------------------------

/**
 * The hashtag decoration extension — the `#tag` viewport-scoped marker (editor-chat.md §2.1).
 *
 * NOTE — completion source is exported separately via `hashtagCompletionSource(store)` and
 * registered by `<EditorViewHost>` together with the wikilink source in a SINGLE
 * `autocompletion({ override: [...] })` call. CM6's `override` field is single-valued — two
 * extensions both calling `autocompletion({ override: ... })` raise "Config merge conflict for
 * field override" at `EditorState.create` time. The decoration plugin stays here because it's
 * a `ViewPlugin` (multi-instance friendly); the completion source moves up to the composition
 * layer so there's exactly one `override` config.
 */
export const hashtagAutocompleteExtension: Extension = hashtagDecorationPlugin;

/**
 * The hashtag completion source — for the editor's single combined `autocompletion()` call.
 * Self-scopes: returns `null` when the cursor is not in a `#` context, so it coexists with
 * the wikilink source via the standard "first non-null source wins" CM6 dispatch.
 */
export function hashtagCompletionSource(store: AutocompleteSourceStore): CompletionSource {
  return makeHashtagCompletionSource(store);
}
